import "@/index.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import { Toaster } from "sonner";
import axios from "axios";
import { setLenis } from "@/lib/scroll";
import Landing from "@/pages/Landing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import InvitePage from "@/pages/InvitePage";
import Circle from "@/pages/Circle";
import Admin from "@/pages/Admin";
import Investors from "@/pages/Investors";
import Account from "@/pages/Account";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Milestones from "@/pages/Milestones";
import MilestoneDetail from "@/pages/MilestoneDetail";
import MemoryDetail from "@/pages/MemoryDetail";
import Family from "@/pages/Family";
import Activity from "@/pages/Activity";
import JoinByInvite from "@/pages/JoinByInvite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthCallback() {
    const location = useLocation();
    const navigate = useNavigate();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
        axios
            .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
            .then((r) => {
                const pending = sessionStorage.getItem("pending_invite");
                if (pending) {
                    sessionStorage.removeItem("pending_invite");
                    navigate(`/join/${pending}`, { replace: true });
                } else {
                    navigate("/dashboard", { state: { user: r.data }, replace: true });
                }
            })
            .catch(() => navigate("/", { replace: true }));
    }, [location, navigate]);

    return null;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/invite/:code" element={<InvitePage />} />
            <Route path="/circle/:sessionId" element={<Circle />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/investors" element={<Investors />} />
            <Route path="/account" element={<Account />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/milestones" element={<Milestones />} />
            <Route path="/milestones/:milestoneId" element={<MilestoneDetail />} />
            <Route path="/memories/:memoryId" element={<MemoryDetail />} />
            <Route path="/family" element={<Family />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/join/:token" element={<JoinByInvite />} />
        </Routes>
    );
}

function App() {
    return (
        <ReactLenis
            root
            options={{ lerp: 0.09, duration: 1.2, smoothWheel: true }}
            ref={(ref) => setLenis(ref?.lenis ?? ref)}
        >
            <div className="grain-overlay" aria-hidden="true" />
            <BrowserRouter>
                <AppRouter />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
        </ReactLenis>
    );
}

export default App;
